// src/pages/tenant/Profile.tsx — the portal's Profile (the phone app's account.tsx + profile/edit.tsx + profile/password.tsx),
// opened from the avatar menu. Everyone: their details (GET /my-profile), edit name and mobile (PATCH /auth/me/profile,
// the sign-up rules), change the password (the auth store's changePassword — POST /auth/change-password, as on every
// screen), sign out. A resident also sees their flat and tenancy and a shortcut to their documents; an applicant their
// booking details, the screening and the budget it covers, and their documents.
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, KeyRound, LogOut, FileText, ShieldCheck, Check, Circle, Eye, EyeOff, CircleCheck } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { formatDate } from '../../lib/viz';
import { Money, SkeletonRows } from '../../components/ui';
import { openStoredFile } from '../../lib/reservations';
import { useResidentPortal } from '../../lib/resident';
import { profileApi, phoneError, type Profile, type ProfileScreening, type ProfileDocument } from '../../lib/portal';
import { TPageHead, TCard, TList, TRow, TFacts, TBanner, TField, TInput } from '../../components/tenant/kit';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { formatPhone } from '../../lib/phone';

type R = Record<string, any>;
const SCREENING: Record<ProfileScreening['status'], { label: string; cls: string }> = {
  none: { label: 'Not yet', cls: 'bg-ink-100 text-ink-600' },
  sent: { label: 'Being checked', cls: 'bg-[#F4E9D4] text-[#7A5A1C]' },
  passed: { label: 'Passed', cls: 'bg-[#E4ECE0] text-[#3F6340]' },
  revoked: { label: 'Revoked', cls: 'bg-[#F0DED9] text-[#8A3F33]' },
};
const errMsg = (e: unknown) => (e instanceof ApiRequestError || e instanceof Error ? e.message : 'Something went wrong. Try again.');

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const resident = useResidentPortal();
  const q = useQuery({ queryKey: ['/my-profile'], queryFn: profileApi.get });
  const tenancies = useQuery({ queryKey: ['my-tenancies'], queryFn: async () => (await api.get<R[]>('/tenancies/mine')).data, enabled: resident });
  const { hash } = useLocation();
  useEffect(() => { if (hash === '#password') document.getElementById('password')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [hash]);

  const p = q.data;
  const a = p?.account;
  const first = a?.firstName ?? user?.firstName ?? '';
  const last = a?.lastName ?? user?.lastName ?? '';
  const name = [first, last].filter(Boolean).join(' ');
  const initials = (name || user?.email || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const t = tenancies.data?.find((x) => ['ACTIVE', 'NOTICE_GIVEN'].includes(x.status)) ?? tenancies.data?.find((x) => !['ENDED', 'CANCELLED'].includes(x.status));

  return (
    <div className="space-y-4 sm:space-y-5">
      <TPageHead overline="Your account" title="Profile" />

      <section className="t-card t-rise flex items-center gap-4 p-5 sm:p-6">
        <span className="t-serif grid h-16 w-16 shrink-0 place-items-center rounded-full text-[26px] text-white sm:h-[72px] sm:w-[72px]" style={{ background: 'var(--t-foil)' }}>{initials}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[20px] font-semibold tracking-[-0.01em] text-ink-900">{name || 'Your account'}</p>
          <p className="truncate text-[14px] text-ink-500">{a?.email ?? user?.email}</p>
          {resident && t && <p className="mt-1 truncate text-[13.5px] font-medium text-bronze-700">Flat {t.unit?.reference} · {t.property?.name}</p>}
        </div>
      </section>
      {q.isError && <TBanner tone="danger">{errMsg(q.error)}</TBanner>}

      <div className="grid items-start gap-4 lg:grid-cols-2 lg:gap-5">
        <div className="space-y-4 lg:space-y-5">
          <DetailsCard p={p} loading={q.isLoading} first={first} last={last} />
          {resident ? <HomeCard t={t} loading={tenancies.isLoading} /> : <ApplicantCards p={p} />}
        </div>
        <div className="space-y-4 lg:space-y-5">
          <PasswordCard email={a?.email ?? user?.email ?? ''} />
          <TCard flush i={5}>
            <TList>
              {resident && <TRow icon={FileText} title="Your documents" sub="Agreement, deposit, certificates, letters" to="/app/my-home/documents" />}
              {!resident && <TRow icon={KeyRound} title="My booking" sub={p?.booking ? `Flat ${p.booking.flat} at ${p.booking.property}` : 'Find a home and book it'} to="/app/my-booking" />}
              <TRow icon={LogOut} tone="danger" title="Sign out" sub="You can sign in again any time" chevron={false} onClick={async () => { await logout(); navigate('/login'); }} />
            </TList>
          </TCard>
        </div>
      </div>
    </div>
  );
}

/** Name, email, mobile — and the edit form in place (the app's profile/edit). */
function DetailsCard({ p, loading, first, last }: { p: Profile | undefined; loading: boolean; first: string; last: string }) {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ first: '', last: '', phone: '' });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const phone = p?.account.phone ?? null;
  const errs = { first: !f.first.trim() ? 'Enter your first name.' : null, last: !f.last.trim() ? 'Enter your last name.' : null, phone: phoneError(f.phone) };

  const start = () => { setF({ first, last, phone: phone ?? '' }); setTried(false); setError(null); setSaved(false); setEditing(true); };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true); setError(null);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    try {
      const me = await profileApi.update({ firstName: f.first.trim(), lastName: f.last.trim(), phone: f.phone.trim() });
      if (me) useAuth.setState({ user: me });
      await qc.invalidateQueries({ queryKey: ['/my-profile'] });
      setEditing(false); setSaved(true);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <TCard i={1} title="Personal details" sub={editing ? 'Your email is how you sign in, so it stays as it is.' : undefined}
      action={!editing && !loading ? <button type="button" onClick={start} className="t-btn t-btn-soft t-btn-sm"><Pencil className="h-4 w-4" />Edit</button> : undefined}>
      {loading ? <SkeletonRows rows={3} cols={1} /> : editing ? (
        <form onSubmit={save} noValidate className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TField label="First name" error={tried ? errs.first : null}><TInput value={f.first} onChange={(e) => setF({ ...f, first: e.target.value })} autoComplete="given-name" invalid={tried && !!errs.first} /></TField>
            <TField label="Last name" error={tried ? errs.last : null} hint="As on your passport or ID"><TInput value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} autoComplete="family-name" invalid={tried && !!errs.last} /></TField>
          </div>
          <TField label="Email"><TInput value={p?.account.email ?? user?.email ?? ''} disabled /></TField>
          <TField label="Mobile" error={tried ? errs.phone : null}><PhoneInput kit="tenant" value={f.phone} onChange={(phone) => setF({ ...f, phone })} invalid={tried && !!errs.phone} /></TField>
          {error && <TBanner tone="danger">{error}</TBanner>}
          <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => setEditing(false)} className="t-btn t-btn-soft">Cancel</button>
            <button type="submit" disabled={busy} className="t-btn t-btn-foil disabled:opacity-60">{busy ? 'Saving…' : 'Save details'}</button>
          </div>
        </form>
      ) : (<>
        <TFacts items={[
          { k: 'Name', v: [first, last].filter(Boolean).join(' ') || <Missing /> },
          { k: 'Email', v: <span className="break-all">{p?.account.email ?? user?.email}</span> },
          { k: 'Mobile', v: formatPhone(phone) || <Missing /> },
        ]} />
        {saved && <p className="mt-3 flex items-center gap-2 text-[14px] font-medium text-[#3F6340]" role="status"><CircleCheck className="h-4 w-4" />Saved.</p>}
      </>)}
    </TCard>
  );
}

const Missing = () => <span className="font-normal text-ink-400">Not added</span>;

/** A resident's flat and tenancy. */
function HomeCard({ t, loading }: { t: R | undefined; loading: boolean }) {
  return (
    <TCard i={2} title="Your home" sub={t?.reference} action={t ? <Link to="/app/my-home" className="inline-flex min-h-[44px] items-center text-[14px] font-semibold text-bronze-700 hover:underline">Open</Link> : undefined}>
      {loading ? <SkeletonRows rows={3} cols={1} /> : !t ? <p className="text-[14px] text-ink-500">Once your tenancy is set up, your flat appears here.</p> : (
        <TFacts items={[
          { k: 'Flat', v: `${t.unit?.reference ?? '—'} · ${t.property?.name ?? ''}` },
          { k: 'From', v: formatDate(t.startDate) },
          { k: 'Until', v: t.endDate ? formatDate(t.endDate) : 'Rolling (periodic)' },
          { k: 'Rent', v: <><Money minor={t.rentMinor} currency={t.rentCurrency} /> a {String(t.rentPeriod ?? 'month').toLowerCase()}</> },
          { k: 'On the tenancy', v: (t.tenants ?? []).map((x: R) => x.name).join(', ') || '—' },
        ]} />
      )}
    </TCard>
  );
}

/** An applicant's booking details, screening and budget, and documents (the app's Profile sections). */
function ApplicantCards({ p }: { p: Profile | undefined }) {
  const [docError, setDocError] = useState<string | null>(null);
  if (!p) return null;
  const s = p.screening; const d = p.details;
  const open = (doc: ProfileDocument) => {
    setDocError(null);
    if (doc.url) { window.open(doc.url, '_blank', 'noopener,noreferrer'); return; }
    if (doc.screeningIndex !== null) void openStoredFile(`/api/v1/my-profile/screening-documents/${doc.screeningIndex}`).catch((e: Error) => setDocError(e.message));
  };
  const badge = SCREENING[s.status];
  return (<>
    <TCard i={2} title="Booking details" sub={d ? undefined : 'Asked for in the first step of a booking'}>
      <TFacts items={[
        { k: 'Date of birth', v: d?.dateOfBirth ? formatDate(d.dateOfBirth) : <Missing /> },
        { k: 'Current address', v: d?.currentAddress || <Missing /> },
        ...(d?.startDate ? [{ k: 'Moving in', v: formatDate(d.startDate) }] : []),
        { k: 'People moving in', v: d?.occupants?.length ? d.occupants.map((o) => o.name).join(', ') : 'Just you' },
      ]} />
    </TCard>

    <TCard i={3} title="Screening" action={<span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${badge.cls}`}>{badge.label}</span>}>
      {s.status === 'none' ? (
        <div className="flex gap-3.5"><span className="t-icon-chip"><ShieldCheck className="h-[19px] w-[19px]" /></span>
          <p className="text-[14px] leading-relaxed text-ink-600">Referencing is done in step 3 of a booking. Once Lotus Living passes it, it counts for your next home too, as long as the rent is within your budget.</p></div>
      ) : (
        <TFacts items={[
          { k: 'Status', v: s.status === 'passed' ? `Passed on ${s.approvedAt ? formatDate(s.approvedAt) : "—"}` : s.status === 'sent' ? `Sent${s.submittedAt ? ` on ${formatDate(s.submittedAt)}` : ""}, being checked` : `Revoked${s.revokedAt ? ` on ${formatDate(s.revokedAt)}` : ''}` },
          { k: 'Employer', v: s.employer || '—' },
          { k: 'Job title', v: s.jobTitle || '—' },
          { k: 'Annual income', v: s.annualIncomeMinor ? <Money minor={s.annualIncomeMinor} currency={s.currency} /> : '—' },
          { k: s.status === 'passed' ? 'Budget it covers' : 'Budget it would cover', v: s.maxRentMinor ? <>Up to <Money minor={s.maxRentMinor} currency={s.currency} /> a month<span className="block text-[12.5px] font-normal text-ink-500">Your income ÷ {s.multiple}</span></> : '—' },
        ]} />
      )}
    </TCard>

    <TCard i={4} title="Documents" sub={p.documents.length ? undefined : 'Nothing uploaded yet'} flush>
      {p.documents.length > 0 && <TList>{p.documents.map((doc) => (
        <TRow key={doc.id} icon={FileText} title={doc.categoryLabel} sub={`${doc.name} · ${formatDate(doc.uploadedAt)}`} onClick={() => open(doc)} />
      ))}</TList>}
      {docError && <p className="px-5 pb-3 text-[13px] text-[#8A3F33]">{docError}</p>}
    </TCard>
  </>);
}

/** Change the password in place: current, new (the rules as a live checklist), again. Other devices are signed out. */
function PasswordCard({ email }: { email: string }) {
  const changePassword = useAuth((s) => s.changePassword);
  const [f, setF] = useState({ current: '', next: '', confirm: '' });
  const [peek, setPeek] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const local = email.split('@')[0]?.toLowerCase() ?? '';
  const lower = f.next.toLowerCase();
  const rules = [
    { label: 'At least 10 characters', ok: f.next.length >= 10 && f.next.length <= 200 },
    { label: 'Not your email address', ok: !!f.next && lower !== email.toLowerCase() && !(local.length >= 4 && lower.includes(local)) },
    { label: 'Not a simple repeat (aaaa…, 1212…)', ok: !!f.next && new Set(f.next).size > 2 },
  ];
  const errs = {
    current: !f.current ? 'Put in your current password.' : null,
    next: rules.every((r) => r.ok) ? null : 'Choose a password that meets every rule below.',
    confirm: !f.confirm ? 'Type your new password again.' : f.confirm !== f.next ? 'The passwords do not match.' : null,
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true); setError(null);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    try {
      await changePassword(f.current, f.next);
      setDone(true); setF({ current: '', next: '', confirm: '' }); setTried(false);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const type = peek ? 'text' : 'password';

  return (
    <div id="password" className="scroll-mt-24">
      <TCard i={3} title="Change password" sub="Any other phone or browser signed in to your account is signed out."
        action={<button type="button" onClick={() => setPeek((v) => !v)} aria-label={peek ? 'Hide passwords' : 'Show passwords'} className="grid h-11 w-11 place-items-center rounded-full text-ink-500 transition hover:bg-ink-50 hover:text-ink-900">{peek ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}</button>}>
        {done && <div className="mb-4"><TBanner tone="good" icon={CircleCheck}>Your password is changed. Use it next time you sign in.</TBanner></div>}
        <form onSubmit={submit} noValidate className="space-y-4">
          <TField label="Current password" error={tried ? errs.current : null}><TInput type={type} value={f.current} onChange={(e) => { setF({ ...f, current: e.target.value }); setDone(false); }} autoComplete="current-password" invalid={tried && !!errs.current} /></TField>
          <div>
            <TField label="New password" error={tried ? errs.next : null}><TInput type={type} value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} autoComplete="new-password" invalid={tried && !!errs.next} /></TField>
            <ul className="mt-2.5 space-y-1.5" aria-label="Password rules">
              {rules.map((r) => (
                <li key={r.label} className={`flex items-center gap-2 text-[13px] ${r.ok ? 'text-[#3F6340]' : 'text-ink-500'}`}>
                  {r.ok ? <Check className="h-4 w-4" /> : <Circle className="h-3.5 w-3.5" />}{r.label}
                </li>
              ))}
            </ul>
          </div>
          <TField label="Confirm new password" error={tried ? errs.confirm : null}><TInput type={type} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} autoComplete="new-password" invalid={tried && !!errs.confirm} /></TField>
          {error && <TBanner tone="danger">{error}</TBanner>}
          <button type="submit" disabled={busy} className="t-btn t-btn-foil w-full disabled:opacity-60"><KeyRound className="h-[18px] w-[18px]" />{busy ? 'Changing…' : 'Change password'}</button>
        </form>
      </TCard>
    </div>
  );
}
